import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useClientDomain } from '../hooks/useClientDomain';
import { DomainKey } from '../config/domains';
import { getDomainConfig as fetchDomainConfig, getAllDomainConfigs, ApiDomainConfig } from '../services/domainConfigService';

interface ThemeContextValue extends ApiDomainConfig {
  domain: DomainKey;
  isLocalDevelopment: boolean;
  allConfigs: Map<string, ApiDomainConfig>;
  refetchConfigs: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { domain, isLocalDevelopment } = useClientDomain();

  const [config, setConfig] = useState<ApiDomainConfig | null>(null);
  const [allConfigs, setAllConfigs] = useState<Map<string, ApiDomainConfig>>(new Map());

  const fetchConfigs = useCallback(() => {
    const isPublicRoute = window.location.pathname.startsWith('/p/');
    const token = localStorage.getItem('authToken');

    if (token && !isPublicRoute) {
      getAllDomainConfigs()
        .then((configs) => {
          const map = new Map<string, ApiDomainConfig>();
          configs.forEach((cfg) => map.set(cfg.hostname, cfg));
          setAllConfigs(map);

          const match = configs.find((c) => c.hostname === domain);
          if (match) setConfig(match);
        })
        .catch(() => {
          fetchDomainConfig(domain)
            .then((cfg: ApiDomainConfig) => {
              setConfig(cfg);
              setAllConfigs(new Map([[cfg.hostname, cfg]]));
            })
            .catch((err) => {
              console.error('[ThemeProvider] Failed to fetch domain config:', err.message);
            });
        });
    } else {
      fetchDomainConfig(domain)
        .then((cfg: ApiDomainConfig) => {
          setConfig(cfg);
          setAllConfigs(new Map([[cfg.hostname, cfg]]));
        }) 
        .catch((err) => {
          console.error('[ThemeProvider] Failed to fetch domain config:', err.message);
        });
    }
  }, [domain]);

  // Initial fetch (guarded against StrictMode double-mount)
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchConfigs();
  }, [fetchConfigs]);

  // Apply theme once config is loaded
  useEffect(() => {
    if (!config) return;

    const root = document.documentElement;
    const { colors, fontFamily, pageTitle, favicon } = config;

    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-primary-hover', colors.primaryHover);
    root.style.setProperty('--font-family-brand', fontFamily);
    root.setAttribute('data-domain', domain);

    document.title = pageTitle;

    const existingFavicon = document.querySelector('link[rel="icon"]');
    if (existingFavicon) {
      existingFavicon.setAttribute('href', favicon);
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      link.href = favicon;
      document.head.appendChild(link);
    }

    if (isLocalDevelopment) {
      console.log('[ThemeProvider] Applied theme for:', domain);
    }
  }, [config, domain, isLocalDevelopment]);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-slate-500" />
      </div>
    );
  }

  const contextValue: ThemeContextValue = {
    ...config,
    domain,
    isLocalDevelopment,
    allConfigs,
    refetchConfigs: fetchConfigs,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
